import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaChevronDown,
  FaChevronUp,
  FaFutbol,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import {
  useSelection,
  SelectedMatch,
  getSelectionKey,
} from './SelectionContext';

export const SelectionOverlay = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    selectedMatches,
    isCollapsed,
    removeMatch,
    clearSelection,
    toggleCollapsed,
  } = useSelection();

  if (selectedMatches.length === 0) {
    return null;
  }

  const formatDate = (isoDate: string) => {
    const formatted = isoDate.replace('T', ' ').slice(0, 16); // yyyy-MM-dd HH:mm
    const [datePart, time] = formatted.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year} · ${time}`;
  };

  const formatBetLabel = (match: SelectedMatch) => {
    if (
      match.PredictionType === 'Result' ||
      match.PredictionType === 'ExactResult'
    ) {
      return match.ExactResult || match.PredictionType;
    }
    return match.PredictionType;
  };

  // Same color logic used for the probability bar in the Home list
  const getProbClass = (prob: number): string => {
    if (prob <= 25) return 'selection-overlay__prob--low';
    if (prob < 40) return 'selection-overlay__prob--medium-low';
    if (prob <= 59) return 'selection-overlay__prob--medium';
    if (prob < 80) return 'selection-overlay__prob--high';
    return 'selection-overlay__prob--very-high';
  };

  return (
    <div
      className={`selection-overlay ${
        isCollapsed ? 'selection-overlay--collapsed' : ''
      }`}
    >
      <header className="selection-overlay__header">
        <FaFutbol className="selection-overlay__icon" />
        <span className="selection-overlay__title">{t('selection.title')}</span>
        <span className="selection-overlay__count">
          {selectedMatches.length}
        </span>

        <button
          type="button"
          className="selection-overlay__clear"
          onClick={clearSelection}
        >
          <FaTrash />
          <span className="selection-overlay__clear-label">
            {t('selection.clear')}
          </span>
        </button>

        <button
          type="button"
          className="selection-overlay__collapse"
          onClick={toggleCollapsed}
          aria-label={
            isCollapsed
              ? (t('selection.expand') as string)
              : (t('selection.collapse') as string)
          }
        >
          {isCollapsed ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </header>

      {!isCollapsed && (
        <ul className="selection-overlay__list">
          {selectedMatches.map((match) => (
            <li
              key={getSelectionKey(match)}
              className="selection-overlay__item"
            >
              <button
                type="button"
                className="selection-overlay__match"
                onClick={() => navigate(`/MatchAnalysis/${match.MatchId}`)}
              >
                <span className="selection-overlay__teams">
                  <span className="selection-overlay__team">{match.Home}</span>
                  <span className="selection-overlay__vs">
                    {t('matchAnalysisPage.vs')}
                  </span>
                  <span className="selection-overlay__team">{match.Away}</span>
                </span>
                <span className="selection-overlay__meta">
                  {match.League} · {formatDate(match.MatchDate)}
                </span>
                {(match.PredictionType ||
                  typeof match.Prob === 'number' ||
                  match.Odd > 0) && (
                  <span className="selection-overlay__bets">
                    {match.PredictionType && (
                      <span className="selection-overlay__bet">
                        {formatBetLabel(match)}
                      </span>
                    )}
                    {typeof match.Prob === 'number' && (
                      <span
                        className={`selection-overlay__prob ${getProbClass(
                          match.Prob,
                        )}`}
                      >
                        {match.Prob}%
                      </span>
                    )}
                    {typeof match.Odd === 'number' && match.Odd > 0 && (
                      <span className="selection-overlay__odd">
                        {match.Odd}
                      </span>
                    )}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="selection-overlay__remove"
                onClick={() => removeMatch(match)}
                aria-label={t('selection.remove') as string}
              >
                <FaTimes />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
